import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Calendar, Package, ArrowLeft } from 'lucide-react';
import { getAllOrders, removeDuplicateOrders } from '../services/orderService';

const MyOrders = () => {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    // Remove any duplicate orders first
    removeDuplicateOrders();
    
    // Load orders from localStorage
    const loadedOrders = getAllOrders();
    setOrders(loadedOrders);
  }, []);

  const formatPrice = (price) => {
    if (typeof price === 'string') {
      return price;
    }
    return `Rs ${price.toLocaleString()}`;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>My Orders - Mughal Ainak Mahal</title>
        </Helmet>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="mb-6">
            <Link
              to="/cart"
              className="flex items-center text-gray-600 hover:text-primary transition-colors duration-200 mb-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Cart
            </Link>
            <h1 className="text-3xl font-bold text-secondary">My Orders</h1>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-12 h-12 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-secondary mb-4">No Orders Yet</h2>
            <p className="text-gray-600 mb-8">
              You haven't placed any orders yet. Start shopping to see your orders here!
            </p>
            <Link
              to="/eyeglasses"
              className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors duration-200"
            >
              Start Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>My Orders - Mughal Ainak Mahal</title>
        <meta name="description" content="View your order history at Mughal Ainak Mahal" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
          <div className="mb-6">
            <Link
              to="/cart"
              className="flex items-center text-gray-600 hover:text-primary transition-colors duration-200 mb-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Cart
            </Link>
            <h1 className="text-3xl font-bold text-secondary">My Orders</h1>
            <p className="text-gray-600 mt-2">You have {orders.length} {orders.length === 1 ? 'order' : 'orders'}</p>
          </div>

        {/* Orders List */}
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.orderNumber} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-secondary mb-1">
                      Order #{order.orderNumber}
                    </h3>
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(order.timestamp)}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">
                      {formatPrice(order.total)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {order.items.reduce((sum, item) => sum + item.quantity, 0)} items
                    </p>
                  </div>
                </div>

                {/* Order Items Preview */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-secondary mb-3">Items:</h4>
                  <div className="space-y-2">
                    {order.items.slice(0, 3).map((item, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-3">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                          <div>
                            <p className="font-medium text-secondary">{item.name}</p>
                            <p className="text-gray-500">×{item.quantity}</p>
                          </div>
                        </div>
                        <p className="font-semibold text-secondary">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <p className="text-sm text-gray-500 italic">
                        + {order.items.length - 3} more item(s)
                      </p>
                    )}
                  </div>
                </div>

                {/* Order Summary */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Customer:</span>
                    <span className="font-semibold">{order.customer.firstName} {order.customer.lastName}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Delivery:</span>
                    <span className="font-semibold text-right max-w-xs">
                      {order.customer.address}, {order.customer.city}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Payment:</span>
                    <span className="font-semibold">{order.paymentMethod || 'Cash on Delivery'}</span>
                  </div>
                  
                  {/* Price Breakdown */}
                  <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold">{formatPrice(order.subtotal || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Shipping:</span>
                      <span className="font-semibold">{formatPrice(order.shipping || 0)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                      <span>Total:</span>
                      <span className="text-primary">{formatPrice(order.total)}</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MyOrders;

